import { Grid2 as Grid, Divider } from "@mui/material";
import { useTranslate } from "@refinedev/core";
import Typography from "@mui/material/Typography";

import { Form } from "@shared/components";
import {
  AgreementsAutocomplete,
  BookCategoriesAutocomplete,
  LevelsAutocomplete,
} from "@shared/components/AutocompleteFields";
import { useDateOptions, useMediaTypeOptions } from "@shared/options";
import { BookAutorAutocomplete } from "@shared/components/AutocompleteFields/BookAutorAutocomplete";
import { useListSectorsQuery } from "@modules/bookSectors/services/queries";

export function BookForm() {
  const translate = useTranslate();
  const { getMediaTypeOptions } = useMediaTypeOptions();
  const { getMonthOptions, getYearOptions } = useDateOptions();
  const { data: sectors = [] } = useListSectorsQuery({ perPage: 100 });

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <Typography variant="h6" color="primary">
          {translate("v1/book_admin.titles.basic_info")}
        </Typography>
        <Divider sx={{ my: 2 }} />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Form.Input
          name="title"
          label={translate("v1/book_admin.fields.title")}
          required
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Form.Input
          name="subtitle"
          label={translate("v1/book_admin.fields.subtitle")}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Form.Input
          name="isbn"
          label={translate("v1/book_admin.fields.isbn")}
          required
        />
      </Grid>

      <Grid size={{ xs: 12 }} sx={{ mt: 4 }}>
        <Typography variant="h6" color="primary">
          {translate("v1/book_admin.titles.type_levels")}
        </Typography>
        <Divider sx={{ my: 2 }} />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Form.Select
          name="sector"
          label={translate("book_category_admin.fields.sector")}
          options={sectors.map((sector) => ({
            label: sector.name,
            value: sector.id,
          }))}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Form.Select
          name="type"
          label={translate("v1/book_admin.fields.type")}
          options={getMediaTypeOptions()}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Form.Select
          name="origin"
          label={translate("v1/book_admin.fields.origin")}
          options={[
            { value: "bookwire", label: "Bookwire" },
            { value: "globo", label: "Globo" },
            { value: "partiu", label: "Partiu" },
          ]}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <LevelsAutocomplete
          name="levels"
          label={translate("v1/book_admin.fields.levels")}
          required
          multiple
        />
      </Grid>

      <Grid size={{ xs: 12 }} sx={{ mt: 4 }}>
        <Typography variant="h6" color="primary">
          {translate("v1/book_admin.titles.publication_agreements")}
        </Typography>
        <Divider sx={{ my: 2 }} />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Form.Input
          name="edition"
          label={translate("v1/book_admin.fields.edition")}
        />
      </Grid>

      <Grid size={{ xs: 6 }}>
        <Form.Input
          name="publishing_city"
          label={translate("v1/book_admin.fields.publishing_city")}
        />
      </Grid>

      <Grid size={{ xs: 6 }}>
        <Form.Input
          name="publishing_country"
          label={translate("v1/book_admin.fields.publishing_country")}
        />
      </Grid>

      <Grid size={{ xs: 6 }}>
        <AgreementsAutocomplete
          name="agreements"
          label={translate("v1/book_admin.fields.agreements")}
          multiple
        />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <BookCategoriesAutocomplete
          name="categories"
          label={translate("v1/book_admin.fields.categories")}
          multiple
        />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <BookAutorAutocomplete
          name="contributors"
          label={translate("v1/book_admin.fields.autor")}
          multiple
        />
      </Grid>

      <Grid size={{ xs: 12 }} sx={{ mt: 4 }}>
        <Typography variant="h6" color="primary">
          {translate("v1/book_admin.titles.files")}
        </Typography>
        <Divider sx={{ my: 2 }} />
      </Grid>

      <Grid size={{ xs: 6 }}>
        <Form.SinglePhotoPicker
          name="cover"
          label={translate("v1/book_admin.fields.cover")}
          shape="square"
          size={150}
        />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Form.SinglePhotoPicker
          name="file"
          label={"Arquivo do Livro/Revista"}
          shape="square"
          size={150}
        />
      </Grid>
      <Grid size={{ xs: 12 }} sx={{ mt: 4 }}>
        <Typography variant="h6" color="primary">
          {translate("v1/book_admin.titles.content")}
        </Typography>
        <Divider sx={{ my: 2 }} />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Form.Editor
          name="description"
          label={translate("v1/book_admin.fields.description")}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Form.Editor
          name="author_biography"
          label={translate("v1/book_admin.fields.author_biography")}
        />
      </Grid>

      <Grid size={{ xs: 12 }} sx={{ mt: 4 }}>
        <Typography variant="h6" color="primary">
          {translate("v1/book_admin.titles.settings")}
        </Typography>
        <Divider sx={{ my: 2 }} />
      </Grid>

      <Grid size={{ xs: 6 }}>
        <Form.Select
          name="month_featured"
          label={translate("v1/book_admin.fields.month_featured")}
          options={getMonthOptions()}
        />
      </Grid>

      <Grid size={{ xs: 6 }}>
        <Form.Select
          name="month_featured_year"
          label={translate("v1/book_admin.fields.month_featured_year")}
          options={getYearOptions()}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Form.Input
          name="price_amount"
          label={translate("v1/book_admin.fields.price_amount")}
        />
      </Grid>

      <Grid size={{ xs: 6 }}>
        <Form.Input
          name="max_number_of_rental_days"
          label={translate("v1/book_admin.fields.max_rental_days")}
        />
      </Grid>

      <Grid size={{ xs: 6 }}>
        <Form.Input
          name="max_amount_of_simultaneous_rent"
          label={translate("v1/book_admin.fields.max_simultaneous_rent")}
        />
      </Grid>

      <Grid size={{ xs: 12 }} sx={{ mt: 4 }}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" color="primary">
          {translate("v1/book_admin.titles.availability")}
        </Typography>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Form.Switch
          name="most_read"
          label={translate("v1/book_admin.fields.most_read")}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Form.Switch
          name="is_pay_per_use"
          label={translate("v1/book_admin.fields.is_pay_per_use")}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Form.Switch
          name="is_for_rent"
          label={translate("v1/book_admin.fields.is_for_rent")}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Form.Switch
          name="is_for_exchange"
          label={translate("v1/book_admin.fields.is_for_exchange")}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Form.Switch
          name="is_for_sale"
          label={translate("v1/book_admin.fields.is_for_sale")}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Form.Switch
          name="is_for_library"
          label={translate("v1/book_admin.fields.is_for_library")}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Form.Switch
          name="is_for_recurring_sale"
          label={translate("v1/book_admin.fields.is_for_recurring_sale")}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Form.Switch
          name="is_best_seller"
          label={translate("v1/book_admin.fields.is_best_seller")}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Form.Switch
          name="is_permanent"
          label="Exibir Sempre"
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Form.Switch
          name="active"
          label={translate("v1/book_admin.fields.active")}
        />
      </Grid>
    </Grid>
  );
}
